import { ReactElement, useCallback, useMemo } from 'react';

import { DebugToolbarModal, Loader } from '@breathelife/ui-components';
import {
  DEFAULT_TIMEZONE_NAME,
  deserializeNodeIdToAnswerPathMap,
  getAllSubsections,
  InsuranceModules,
  Localized,
  NodeIdToAnswerPathMap,
  PivotAnswerResolver,
  QuestionnaireBlueprint,
  QuestionnaireDefinition,
  QuestionnaireEngine,
  RenderingQuestionnaire,
  RenderingType,
  Timezone,
  VersionedAnswers,
} from '@zinnia/form-engine-sdk';

import { Language } from '@breathelife/types';
import { Application } from '../../Models/Application';
import { useFetchCarrierQuestionnaireQuery } from '../../ReactQuery/CarrierQuestionnaires/carrierQuestionnaire.queries';
import { useUpdateApplicationAnswersUntilMutation } from '../../ReactQuery/DebugToolbar/debugToolbar.mutations';

type Props = {
  selectedApplication: Application;
  onClose: () => void;
};

export function DebugToolbarModalViewContainer(props: Props): ReactElement {
  const { selectedApplication, onClose } = props;

  const { isLoading: isLoadingQuestionnaire, data: questionnaireData } = useFetchCarrierQuestionnaireQuery(
    selectedApplication?.id,
    {},
  );
  const questionnaire = questionnaireData?.questionnaire;
  const blueprint = questionnaireData?.blueprint;
  const serializedNodeIdToAnswerPathMap = questionnaireData?.nodeIdToAnswerPath;
  const currentQuestionnaireVersion = questionnaireData?.versionId;

  const nodeIdToAnswerPathMap = useMemo(
    () => serializedNodeIdToAnswerPathMap && deserializeNodeIdToAnswerPathMap(serializedNodeIdToAnswerPathMap),
    [serializedNodeIdToAnswerPathMap],
  );

  const stepIds = useMemo(() => {
    if (!selectedApplication?.id || !questionnaire || !nodeIdToAnswerPathMap || !blueprint) return;
    if (selectedApplication?.currentQuestionnaireVersionId !== currentQuestionnaireVersion) return;
    const timezoneResult = Timezone.from(selectedApplication.timezone || DEFAULT_TIMEZONE_NAME);
    if (timezoneResult.success === false) {
      throw new Error("Could not create a timezone from either the application's one or the default onemptied.");
    }

    const renderingQuestionnaire = buildRenderingQuestionnaire(
      questionnaire,
      blueprint,
      nodeIdToAnswerPathMap,
      timezoneResult.value,
      selectedApplication.submissionDate?.toString() || null,
      selectedApplication.lang,
    );

    return getAllSubsections(renderingQuestionnaire).map((subsection) => subsection.id);
  }, [selectedApplication, currentQuestionnaireVersion, questionnaire, nodeIdToAnswerPathMap]);

  const updateApplicationAnswersUntilMutation = useUpdateApplicationAnswersUntilMutation({
    onSuccess: () => {
      onClose();

      // the timeout is needed because the AA is not setting answers coming from an action outside the AA until the AA is closed
      //TODO: https://breathelife.atlassian.net/browse/DEV-6727
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    },
  });

  const updateApplicationAnswers = useCallback(
    async (stepId: string) => {
      if (!selectedApplication?.id) return;

      updateApplicationAnswersUntilMutation.mutate({
        applicationId: selectedApplication.id,
        subsectionId: stepId,
      });
    },
    [selectedApplication, updateApplicationAnswersUntilMutation],
  );

  if (isLoadingQuestionnaire) return <Loader />;

  return <DebugToolbarModal onClose={onClose} steps={stepIds ?? []} onStepSelect={updateApplicationAnswers} />;
}

function buildRenderingQuestionnaire(
  questionnaire: Localized<QuestionnaireDefinition>,
  blueprint: QuestionnaireBlueprint,
  nodeIdToAnswerPathMap: NodeIdToAnswerPathMap,
  timezone: Timezone,
  currentDateOverride: string | null,
  language: Language,
): RenderingQuestionnaire {
  const answerResolver = PivotAnswerResolver.from(
    new VersionedAnswers({ v1: {}, v2: {} }),
    nodeIdToAnswerPathMap,
    blueprint,
    'v1',
  );
  const questionnaireEngine = QuestionnaireEngine.from({
    blueprint,
    questionnaire,
    answerResolver,
    context: {
      insuranceModules: [InsuranceModules.insuranceApplication],
    },
    timezone,
    answerChangedSubscribers: [],
    onRenderingQuestionnaireChanged: () => {},
    language,
    renderingOptions: {
      // TODO: Remove filter
      renderingType: RenderingType.consumerSummary,
      displayErrors: false,
    },
    currentDateOverride,
  });

  return questionnaireEngine.renderingQuestionnaire;
}
